import { supabase } from "@/integrations/supabase/client";

export interface ReferralConfig {
  id: string;
  supplier_id: string;
  goal_referrals: number;
  reward_description: string;
  referral_discount_percent: number;
  is_active: boolean;
}

export interface ReferralCode {
  id: string;
  code: string;
  client_user_id: string;
  supplier_id: string;
  referral_count: number;
  event_id?: string | null;
  created_at: string;
}

// Generate a unique referral code
const generateCode = (supplierSlug: string): string => {
  const hex = Math.random().toString(36).slice(2, 8).toUpperCase();
  const prefix = supplierSlug.slice(0, 3).toUpperCase();
  return `${prefix}-${hex}`;
};

// Fetch or create referral code for a client+supplier pair
export const getOrCreateReferralCode = async (
  clientUserId: string,
  supplierId: string,
  supplierSlug: string,
  eventId?: string
): Promise<ReferralCode | null> => {
  try {
    // Check existing
    const { data: existing } = await (supabase as any)
      .from('referral_codes')
      .select('*')
      .eq('client_user_id', clientUserId)
      .eq('supplier_id', supplierId)
      .maybeSingle();

    if (existing) {
      // Always update event_id when provided (links the code to the current origin event)
      if (eventId && existing.event_id !== eventId) {
        const { error: updateError } = await (supabase as any)
          .from('referral_codes')
          .update({ event_id: eventId })
          .eq('id', existing.id);

        if (updateError) {
          console.error('Error updating referral code event_id:', updateError);
        } else {
          existing.event_id = eventId;
        }
      }
      return existing;
    }

    // Create new
    const code = generateCode(supplierSlug);
    const { data, error } = await (supabase as any)
      .from('referral_codes')
      .insert({
        code,
        client_user_id: clientUserId,
        supplier_id: supplierId,
        ...(eventId ? { event_id: eventId } : {}),
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating referral code:', error);
      return null;
    }
    return data;
  } catch (err) {
    console.error('Error in getOrCreateReferralCode:', err);
    return null;
  }
};

// Lookup referral code details
export const lookupReferralCode = async (code: string): Promise<ReferralCode | null> => {
  try {
    const { data } = await (supabase as any)
      .from('referral_codes')
      .select('*')
      .eq('code', code)
      .maybeSingle();
    return data || null;
  } catch {
    return null;
  }
};

// Increment referral count
export const incrementReferralCount = async (codeId: string, currentCount: number): Promise<void> => {
  await (supabase as any)
    .from('referral_codes')
    .update({ referral_count: currentCount + 1 })
    .eq('id', codeId);
};

// Fetch supplier referral config
export const fetchReferralConfig = async (supplierId: string): Promise<ReferralConfig | null> => {
  try {
    const { data } = await (supabase as any)
      .from('supplier_referral_config')
      .select('*')
      .eq('supplier_id', supplierId)
      .maybeSingle();
    return data || null;
  } catch {
    return null;
  }
};

// Upsert supplier referral config
export const upsertReferralConfig = async (
  supplierId: string,
  config: Partial<ReferralConfig>
): Promise<boolean> => {
  try {
    const { error } = await (supabase as any)
      .from('supplier_referral_config')
      .upsert({
        supplier_id: supplierId,
        goal_referrals: config.goal_referrals ?? 5,
        reward_description: config.reward_description ?? '',
        referral_discount_percent: config.referral_discount_percent ?? 0,
        is_active: config.is_active ?? true,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'supplier_id' });

    if (error) {
      console.error('Error upserting referral config:', error);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

// ─────────────────────────────────────────────────────────────
// Unified referral link generation (single source of truth)
// Used by Admin > Indicação, Eventos > Entregue card/list and the Kanban modal.
// ─────────────────────────────────────────────────────────────

const normalizeStatus = (value?: string) =>
  (value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const QUALIFYING_REFERRAL_STATUSES = new Set(['programado', 'em preparacao', 'entregue']);

export interface ResolveSupplierResult {
  supplierId: string;
  supplierSlug: string;
}

/**
 * Resolves the current user's supplier id + slug directly from Supabase,
 * accepting optional fallbacks from local state. Returns null when nothing valid is found.
 */
export const resolveCurrentSupplier = async (
  fallbackSupplierId?: string | null,
  fallbackSupplierSlug?: string | null,
  userId?: string | null
): Promise<ResolveSupplierResult | null> => {
  let supplierId = fallbackSupplierId || '';
  let supplierSlug = fallbackSupplierSlug && fallbackSupplierSlug !== 'loja' ? fallbackSupplierSlug : '';

  // Try by user_id first when available
  if ((!supplierId || !supplierSlug) && userId) {
    const { data } = await (supabase as any)
      .from('suppliers')
      .select('id, slug')
      .eq('user_id', userId)
      .maybeSingle();
    if (data) {
      supplierId = supplierId || data.id;
      supplierSlug = supplierSlug || data.slug || '';
    }
  }

  // Fallback by supplierId only (e.g. modal that already has supplierId but no slug)
  if (supplierId && !supplierSlug) {
    const { data } = await (supabase as any)
      .from('suppliers')
      .select('slug')
      .eq('id', supplierId)
      .maybeSingle();
    supplierSlug = data?.slug || '';
  }

  if (!supplierId || !supplierSlug) return null;
  return { supplierId, supplierSlug };
};

/**
 * Picks the best origin event id for a referral code, replicating the rule
 * used in Administração > Gerar link de indicação:
 *   1. Prefer the explicit eventId when provided.
 *   2. Otherwise pick the most recent qualifying event for that client+supplier.
 *   3. Fallback to the most recent event of any status.
 */
const resolveOriginEventId = async (
  clientUserId: string,
  supplierId: string,
  preferredEventId?: string | null
): Promise<string | undefined> => {
  if (preferredEventId) return preferredEventId;

  const { data: clientEvents } = await (supabase as any)
    .from('events')
    .select('id, status, event_date, created_at')
    .or(`supplier_id.eq.${supplierId},created_by_supplier_id.eq.${supplierId}`)
    .eq('user_id', clientUserId)
    .is('parent_event_id', null)
    .order('event_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });

  return (
    (clientEvents || []).find((evt: any) =>
      QUALIFYING_REFERRAL_STATUSES.has(normalizeStatus(evt.status))
    )?.id || clientEvents?.[0]?.id
  );
};

export interface BuildReferralLinkInput {
  /** Optional — when missing we try to resolve via event_client_info → profile */
  clientUserId?: string | null;
  fallbackSupplierId?: string | null;
  fallbackSupplierSlug?: string | null;
  authUserId?: string | null;
  preferredEventId?: string | null;
  /** Used as fallback to look up the client in event_client_info */
  eventId?: string | null;
}

/**
 * Tries to resolve a registered platform user for a given event by looking up
 * event_client_info → profile (by email, then by phone). Returns null if not found.
 */
export const resolveClientUserIdFromEvent = async (
  eventId: string
): Promise<string | null> => {
  try {
    // 1) Direct lookup on events table — fastest and works for child events
    //    that don't have their own event_client_info row.
    const { data: evt } = await (supabase as any)
      .from('events')
      .select('user_id, parent_event_id')
      .eq('id', eventId)
      .maybeSingle();

    if (evt?.user_id) return evt.user_id;

    // 2) Fallback to event_client_info on this event or its parent
    const lookupId = evt?.parent_event_id || eventId;
    const { data: ci } = await (supabase as any)
      .from('event_client_info')
      .select('client_email, client_phone')
      .eq('event_id', lookupId)
      .maybeSingle();

    if (!ci) return null;

    const email = (ci.client_email || '').trim().toLowerCase();
    if (email) {
      const { data: byEmail } = await (supabase as any)
        .from('profile')
        .select('id')
        .ilike('email', email)
        .maybeSingle();
      if (byEmail?.id) return byEmail.id;
    }

    const phoneDigits = (ci.client_phone || '').replace(/\D/g, '');
    if (phoneDigits.length >= 8) {
      const { data: profiles } = await (supabase as any)
        .from('profile')
        .select('id, phone');
      const match = (profiles || []).find(
        (p: any) => (p.phone || '').replace(/\D/g, '') === phoneDigits
      );
      if (match?.id) return match.id;
    }
    return null;
  } catch {
    return null;
  }
};

export interface BuildReferralLinkResult {
  link: string;
  code: string;
  supplierSlug: string;
  supplierId: string;
}

/**
 * Single source of truth for building an MGM referral link.
 * Replicates the behavior of Administração > Gerar link de indicação so the
 * Eventos > Entregue button always produces an identical URL.
 */
export const buildReferralLinkForClient = async (
  input: BuildReferralLinkInput
): Promise<BuildReferralLinkResult | null> => {
  const supplier = await resolveCurrentSupplier(
    input.fallbackSupplierId,
    input.fallbackSupplierSlug,
    input.authUserId
  );
  if (!supplier) return null;

  // Resolve client user id (with fallback via event_client_info → profile)
  let clientUserId = input.clientUserId || null;
  if (!clientUserId && input.eventId) {
    clientUserId = await resolveClientUserIdFromEvent(input.eventId);
  }
  if (!clientUserId) return null;

  const originEventId = await resolveOriginEventId(
    clientUserId,
    supplier.supplierId,
    input.preferredEventId
  );

  const code = await getOrCreateReferralCode(
    clientUserId,
    supplier.supplierId,
    supplier.supplierSlug,
    originEventId
  );
  if (!code) return null;

  const link = `${window.location.origin}/loja/${supplier.supplierSlug}?mgm=${code.code}`;
  return { link, code: code.code, supplierSlug: supplier.supplierSlug, supplierId: supplier.supplierId };
};

/**
 * Tries clipboard with a textarea fallback. Returns true if any copy succeeded.
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textArea);
      return ok;
    } catch {
      return false;
    }
  }
};

// Fetch all referral codes for a supplier (with client names)
export const fetchSupplierReferrals = async (supplierId: string): Promise<any[]> => {
  try {
    const { data } = await (supabase as any)
      .from('referral_codes')
      .select('*, profile:client_user_id(full_name, email, phone)')
      .eq('supplier_id', supplierId)
      .order('referral_count', { ascending: false });
    return data || [];
  } catch {
    return [];
  }
};

/**
 * Calculates the "real" referral count for a specific referral code, 
 * counting only events with "Programado" (or equivalent) status.
 */
export const fetchRealReferralCount = async (clientUserId: string, supplierId: string): Promise<number> => {
  try {
    const qualifyingStatuses = [
      'programado', 
      'programado - contrato assinado', 
      'programado - aguardando assinatura', 
      'reprogramado', 
      'redimensionado', 
      'prorrogado'
    ];
    
    // We count events where referred_by matches the clientUserId (who is the referrer)
    // and supplier matches, and status is qualifying.
    const { count, error } = await supabase
      .from('events')
      .select('*', { count: 'exact', head: true })
      .eq('referred_by', clientUserId)
      .or(`supplier_id.eq.${supplierId},created_by_supplier_id.eq.${supplierId}`)
      .in('status', qualifyingStatuses);
      
    if (error) throw error;
    return count || 0;
  } catch (err) {
    console.error('Error fetching real referral count:', err);
    return 0;
  }
};
